import {
  OrderImportRequest,
  postV1CompaniesCompanyIdImportsOrders,
  WaypointType,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';

import { ImportedOrder } from '~hooks/useOrders/models';
import { CSVColumn } from '~utils/csv/csvProcessor';

import {
  buildImportSite,
  numberOrUndefined,
  trimmedOrUndefined,
  withoutEmptyValues,
} from './importPayload';
import { EntityCSVConfig } from './types';
import { normalizeUnitOfMeasure, VALID_ORDER_UNITS_OF_MEASURE } from './unitOfMeasure';

// The columns whose values are parsed with numberOrUndefined in buildOrderImportRequest
const NUMERIC_COLUMN_KEYS = ['truck_count', 'expected_total_loads', 'quantity'] as const;

const ordersColumns = (dispatchNumberLabel: string): CSVColumn[] => [
  {
    key: 'external_id',
    header: 'external_id',
    required: false,
    exampleValue: 'ORDER-001',
    description:
      'Unique ID for the order. Provided = re-import updates the same order. Blank = a new ID is generated and re-uploads create duplicates.',
  },
  {
    key: 'load_at',
    header: 'load_at',
    required: true,
    exampleValue: '2026-08-10T07:00',
    description: 'When loading starts. Read in the company time zone without an offset.',
  },
  {
    key: 'customer_name',
    header: 'customer_name',
    required: false,
    exampleValue: 'Acme Paving',
    description:
      'Customer account name. Created when no match is found. Leave blank to use the customer of the matching project.',
  },
  {
    key: 'customer_external_id',
    header: 'customer_external_id',
    required: false,
    exampleValue: 'CUST-001',
    description: 'Your internal reference ID for the customer',
  },
  {
    key: 'order_name',
    header: 'order_name',
    required: false,
    exampleValue: 'Main St resurfacing',
    description: 'Name of the order',
  },
  {
    key: 'dispatch_number',
    header: 'dispatch_number',
    label: dispatchNumberLabel,
    headerAliases: ['PO', 'Customer Ref', 'Customer Ref./PO'],
    required: false,
    exampleValue: '',
    description:
      'Customer reference or PO number of the order, also used to find the project',
  },
  {
    key: 'truck_count',
    header: 'truck_count',
    required: false,
    exampleValue: '3',
    description: 'Number of trucks requested',
  },
  {
    key: 'expected_total_loads',
    header: 'expected_total_loads',
    required: false,
    exampleValue: '12',
    description: 'Expected total loads for the order',
  },
  {
    key: 'quantity',
    header: 'quantity',
    required: false,
    exampleValue: '250',
    description: 'Ordered quantity',
  },
  {
    key: 'unit_of_measure',
    header: 'unit_of_measure',
    required: false,
    exampleValue: 'Tonne',
    description: 'Unit for the quantity, for example Tonne or Yard',
  },
  {
    key: 'material_name',
    header: 'material_name',
    required: false,
    exampleValue: '3/4 Crushed Stone',
    description: 'Material name. Created when no match is found.',
  },
  {
    key: 'material_external_id',
    header: 'material_external_id',
    required: false,
    exampleValue: '',
    description: 'Your internal reference ID for the material',
  },
  {
    key: 'equipment_type_name',
    header: 'equipment_type_name',
    required: false,
    exampleValue: 'Tri Axle',
    description: 'Equipment type name. Created when no match is found.',
  },
  {
    key: 'project_external_id',
    header: 'project_external_id',
    required: false,
    exampleValue: '',
    description: 'External ID of an existing project to attach the order to',
  },
  {
    key: 'project_name',
    header: 'project_name',
    required: false,
    exampleValue: '',
    description:
      'Name of an existing project, used when the project external ID is blank or does not match',
  },
  {
    key: 'contract_number',
    header: 'contract_number',
    required: false,
    exampleValue: '',
    description: 'Contract number of an existing project, used to find the project',
  },
  {
    key: 'pickup_site_name',
    header: 'pickup_site_name',
    required: false,
    exampleValue: 'North Quarry',
    description: 'Pickup site name. Created when no match is found.',
  },
  {
    key: 'pickup_site_external_id',
    header: 'pickup_site_external_id',
    required: false,
    exampleValue: '',
    description: 'Your internal reference ID for the pickup site',
  },
  {
    key: 'pickup_site_address',
    header: 'pickup_site_address',
    required: false,
    exampleValue: '123 Quarry Rd, Austin, TX 78701',
    description: 'Full address used to geocode a newly created pickup site',
  },
  {
    key: 'dropoff_site_name',
    header: 'dropoff_site_name',
    required: false,
    exampleValue: 'Main St Job Site',
    description: 'Dropoff site name. Created when no match is found.',
  },
  {
    key: 'dropoff_site_external_id',
    header: 'dropoff_site_external_id',
    required: false,
    exampleValue: '',
    description: 'Your internal reference ID for the dropoff site',
  },
  {
    key: 'dropoff_site_address',
    header: 'dropoff_site_address',
    required: false,
    exampleValue: '500 Main St, Austin, TX 78701',
    description: 'Full address used to geocode a newly created dropoff site',
  },
  {
    key: 'foreman_email',
    header: 'foreman_email',
    required: false,
    exampleValue: '',
    description: "Must match an existing user's email; unmatched emails are skipped",
  },
  {
    key: 'job_start_at',
    header: 'job_start_at',
    required: false,
    exampleValue: '',
    description: 'When jobs start. Read in the company time zone without an offset.',
  },
  {
    key: 'notes',
    header: 'notes',
    required: false,
    exampleValue: '',
    description: 'Notes for the order',
  },
  {
    key: 'job_notes',
    header: 'job_notes',
    required: false,
    exampleValue: '',
    description: 'Notes copied to each job',
  },
];

type OrderImportSite = NonNullable<OrderImportRequest['sites']>[number];

function buildOrderImportRequest(row: Record<string, string>): OrderImportRequest {
  const sites = [
    buildImportSite(
      WaypointType.PICKUP,
      row.pickup_site_name,
      row.pickup_site_external_id,
      row.pickup_site_address,
    ),
    buildImportSite(
      WaypointType.DROP_OFF,
      row.dropoff_site_name,
      row.dropoff_site_external_id,
      row.dropoff_site_address,
    ),
  ].filter((site): site is OrderImportSite => site !== undefined);

  const foremanEmail = trimmedOrUndefined(row.foreman_email);
  const customerName = trimmedOrUndefined(row.customer_name);
  const project = withoutEmptyValues({
    external_id: trimmedOrUndefined(row.project_external_id),
    name: trimmedOrUndefined(row.project_name),
    contract_number: trimmedOrUndefined(row.contract_number),
    dispatch_number: trimmedOrUndefined(row.dispatch_number),
  });

  const request: OrderImportRequest = {
    // A generated ID keeps the row importable, but re-uploading the same file
    // then creates a second order because there is no key to match on.
    external_id: trimmedOrUndefined(row.external_id) ?? `csv-${crypto.randomUUID()}`,
    load_at: row.load_at?.trim() ?? '',
    ...withoutEmptyValues({
      name: trimmedOrUndefined(row.order_name),
      dispatch_number: trimmedOrUndefined(row.dispatch_number),
      job_start_at: trimmedOrUndefined(row.job_start_at),
      truck_count: numberOrUndefined(row.truck_count),
      expected_total_loads: numberOrUndefined(row.expected_total_loads),
      quantity: numberOrUndefined(row.quantity),
      unit_of_measure: normalizeUnitOfMeasure(row.unit_of_measure),
      notes: trimmedOrUndefined(row.notes),
      job_notes: trimmedOrUndefined(row.job_notes),
      material: withoutEmptyValues({
        name: trimmedOrUndefined(row.material_name),
        external_id: trimmedOrUndefined(row.material_external_id),
      }),
      equipment_type: withoutEmptyValues({
        name: trimmedOrUndefined(row.equipment_type_name),
      }),
    }),
  };

  if (customerName) {
    request.customer = {
      name: customerName,
      ...withoutEmptyValues({
        external_id: trimmedOrUndefined(row.customer_external_id),
      }),
    };
  }

  if (project) {
    request.project = project;
  }

  if (foremanEmail) {
    request.foremen = [{ email: foremanEmail }];
  }

  if (sites.length > 0) {
    request.sites = sites;
  }

  return request;
}

async function importOrderRow(
  companyId: string,
  row: Record<string, string>,
): Promise<ImportedOrder> {
  const response = await postV1CompaniesCompanyIdImportsOrders({
    path: { 'company-id': companyId },
    body: buildOrderImportRequest(row),
  });

  return ImportedOrder.parse(response.data.data);
}

export const createOrdersConfig = (
  dispatchNumberLabel: string,
): EntityCSVConfig<ImportedOrder> => ({
  entityType: 'orders',
  confirmsColumnMapping: true,
  importInstructions: t('csv_import_export.import_instructions.orders'),
  // Sequential: rows commonly share customers/materials/sites, and imports are upserts by external_id
  importBatchSize: 1,
  apiFieldToColumn: {
    'Order/name': 'order_name',
    'Order/load_at': 'load_at',
    'Order/job_start_at': 'job_start_at',
    'Account/name': 'customer_name',
    'Account/external_id': 'customer_external_id',
    'Material/name': 'material_name',
    'EquipmentType/name': 'equipment_type_name',
  },
  columns: ordersColumns(dispatchNumberLabel),

  create: (companyId: string, row: Record<string, string>) =>
    importOrderRow(companyId, row),

  transformImport: (row: Record<string, string>): Record<string, unknown> =>
    buildOrderImportRequest(row),

  summarizeResults: (succeeded: ImportedOrder[]) => {
    const withoutOrder = succeeded.filter((imported) => !imported.orderId).length;

    return withoutOrder > 0
      ? t('csv_import_export.orders_not_created', { count: withoutOrder })
      : null;
  },

  rowLabel: (row: Record<string, string>) =>
    trimmedOrUndefined(row.external_id) ??
    trimmedOrUndefined(row.order_name) ??
    trimmedOrUndefined(row.customer_name),

  validateRow: (row: Record<string, string>): string[] => {
    const errors: string[] = [];

    if (!row.load_at?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'load_at' }));
    } else if (!dayjs(row.load_at.trim()).isValid()) {
      errors.push(t('csv_import_export.errors.invalid_date', { field: 'load_at' }));
    }

    const identifiesCustomerOrProject = [
      'customer_name',
      'project_external_id',
      'project_name',
      'contract_number',
      'dispatch_number',
    ].some((field) => row[field]?.trim());
    if (!identifiesCustomerOrProject) {
      errors.push(t('csv_import_export.errors.customer_or_project_required'));
    }

    if (row.job_start_at?.trim() && !dayjs(row.job_start_at.trim()).isValid()) {
      errors.push(t('csv_import_export.errors.invalid_date', { field: 'job_start_at' }));
    }

    if (row.unit_of_measure?.trim() && !normalizeUnitOfMeasure(row.unit_of_measure)) {
      errors.push(
        t('csv_import_export.errors.invalid_enum', {
          field: 'unit_of_measure',
          values: VALID_ORDER_UNITS_OF_MEASURE.join(', '),
        }),
      );
    }

    NUMERIC_COLUMN_KEYS.forEach((field) => {
      const value = row[field]?.trim();
      if (value && isNaN(Number(value))) {
        errors.push(t('csv_import_export.errors.invalid_number', { field }));
      }
    });

    return errors;
  },
});
